import type { Middleware, Placement, ReferenceType, UseFloatingReturn } from '@floating-ui/react';
import { autoUpdate, useFloating } from '@floating-ui/react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import type { NodeKey } from 'lexical';
import { useEffect, useLayoutEffect, useMemo } from 'react';

/**
 * Positions a floating element against a node's DOM, looked up by key on every measure.
 * - Lexical can replace a node's element (e.g. a header toggle swaps `td` for `th`); holding the
 *   element itself would leave the popup anchored to a detached node in the page corner.
 * - Re-measures after every editor update, since edits move nodes without a scroll or resize.
 */
export const useNodeAnchoredFloating = (
  nodeKey: NodeKey,
  options: { placement: Placement; middleware: Middleware[] },
): UseFloatingReturn<ReferenceType> => {
  const [editor] = useLexicalComposerContext();

  const reference = useMemo(
    () => ({
      getBoundingClientRect: () => editor.getElementByKey(nodeKey)?.getBoundingClientRect() ?? new DOMRect(),
      contextElement: editor.getRootElement() ?? undefined,
    }),
    [editor, nodeKey],
  );

  const floating = useFloating({ ...options, strategy: 'fixed', whileElementsMounted: autoUpdate });
  const { refs, update } = floating;

  useLayoutEffect(() => refs.setPositionReference(reference), [refs, reference]);
  useEffect(() => editor.registerUpdateListener(() => update()), [editor, update]);

  return floating;
};
